
import { useState, useEffect } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { PersonaAwareHeader } from '@/utils/headerUtils';
import { useProduct } from '@/hooks/useProduct';
import { Button } from '@/components/ui/button';
import { ArrowLeft, FileText, Download, Upload, Check } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { CertificationDocuments } from '@/components/certification/CertificationDocuments';
import { ProductDetails } from '@/components/certification/ProductDetails';
import { FormulaDetails } from '@/components/certification/FormulaDetails';
import { FacilityDetails } from '@/components/certification/FacilityDetails';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const CertificationManagement = () => {
  const { portfolioId, productId } = useParams<{ portfolioId: string; productId: string }>();
  const location = useLocation();
  const { product, isLoading, error, refetch } = useProduct(productId || '');
  const [readyForReview, setReadyForReview] = useState(false);
  
  const certification = location.state?.certification || 'green-seal';

  // Update certification status based on ready for review toggle and certification selection
  const updateCertificationStatus = async (isReady: boolean) => {
    if (!productId) return;

    let newStatus = 'pending';
    
    if (!certification || certification === '') {
      newStatus = 'pending'; // No certification selected
    } else if (isReady) {
      newStatus = 'in_progress'; // Ready for review (we'll use in_progress as closest match)
    } else {
      newStatus = 'in_progress'; // Certification system selected but not ready
    }

    try {
      const { error } = await supabase
        .from('products')
        .update({ certification_status: newStatus })
        .eq('id', productId);

      if (error) throw error;

      // Refetch product data to update UI
      refetch();
      
      if (isReady) {
        toast.success('Product marked as ready for review');
      }
    } catch (error: any) {
      console.error('Error updating certification status:', error);
      toast.error('Failed to update certification status');
    }
  };

  const handleReadyForReviewChange = (checked: boolean) => {
    setReadyForReview(checked);
    updateCertificationStatus(checked);
  };

  // Update status when certification system is selected/changed
  useEffect(() => {
    if (product && certification) {
      updateCertificationStatus(readyForReview);
    }
  }, [certification, product]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <PersonaAwareHeader />
        <main className="container py-12">
          <div className="flex justify-center items-center h-64">
            <p>Loading certification management...</p>
          </div>
        </main>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-background">
        <PersonaAwareHeader />
        <main className="container py-12">
          <div className="bg-destructive/10 p-4 rounded-md text-destructive mb-6">
            Product not found or you don't have access to view it.
          </div>
          <Link to={`/client/portfolios/${portfolioId}/products/${productId}`}>
            <Button variant="outline" className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Product
            </Button>
          </Link>
        </main>
      </div>
    );
  }

  const getCertificationTitle = (cert: string) => {
    const titles: Record<string, string> = {
      'green-seal': 'Green Seal Certification',
      'epa-safer-choice': 'EPA Safer Choice Certification',
      'cradle-to-cradle': 'Cradle to Cradle Certification'
    };
    return titles[cert] || 'Certification Management';
  };

  return (
    <div className="min-h-screen bg-background">
      <PersonaAwareHeader />
      
      <main className="container py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to={`/client/portfolios/${portfolioId}/products/${productId}`}>
              <Button variant="outline" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Product
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold">{getCertificationTitle(certification)}</h1>
              <p className="text-muted-foreground">Managing certification for {product.name}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Switch
                id="ready-for-review"
                checked={readyForReview}
                onCheckedChange={handleReadyForReviewChange}
              />
              <Label htmlFor="ready-for-review" className="text-sm font-medium">
                Ready for Review
              </Label>
            </div>
            {readyForReview && (
              <Badge variant="default" className="bg-green-100 text-green-800">
                <Check className="h-3 w-3 mr-1" />
                Ready
              </Badge>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Product Information */}
          <div className="lg:col-span-1 space-y-6">
            <ProductDetails product={product} />
            <FormulaDetails productId={product.id} />
            <FacilityDetails productId={product.id} />
          </div>

          {/* Right Column - Required Documents */}
          <div className="lg:col-span-2">
            <CertificationDocuments 
              productId={product.id}
              certification={certification}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default CertificationManagement;
